import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import DashboardLayout from '../../components/DashboardLayout'
import { enrollmentService, courseService, certificateService } from '../../api'
import { CheckCircle, Circle, ChevronRight, ArrowLeft, BookOpen, Award, ClipboardCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { isEnrollmentCompleted } from '../../utils/enrollmentHelpers'
import '../../styles/assessment-form.css'

function EnrollmentDetail() {
    const { id } = useParams()
    const [enrollment, setEnrollment] = useState(null)
    const [subjects, setSubjects] = useState([])
    const [progress, setProgress] = useState(null)
    const [loading, setLoading] = useState(true)
    const [expanded, setExpanded] = useState({})
    const [certificate, setCertificate] = useState(null)

    useEffect(() => {
        enrollmentService.getEnrollmentDetail(id)
            .then(async (res) => {
                const enr = res.data
                setEnrollment(enr)
                const courseId = enr.course?.id || enr.course
                if (courseId) {
                    const [subRes, progressRes] = await Promise.all([
                        courseService.getSubjects({ course: courseId }),
                        enrollmentService.getEnrollmentProgress(id).catch(() => null),
                    ])
                    const subData = subRes.data.results || subRes.data
                    if (progressRes?.data) setProgress(progressRes.data)

                    const subjectsWithLessons = await Promise.all(
                        subData.map(async (subject) => {
                            const lesRes = await courseService.getLessons({ subject: subject.id })
                            return { ...subject, lessons: lesRes.data.results || lesRes.data }
                        })
                    )
                    setSubjects(subjectsWithLessons)

                    if (isEnrollmentCompleted(enr)) {
                        try {
                            const certRes = await certificateService.getCertificates({ course: courseId })
                            const certs = certRes.data.results || certRes.data
                            if (certs && certs.length > 0) setCertificate(certs[0])
                        } catch (err) {
                            console.error('Failed to load certificate', err)
                        }
                    }
                }
            })
            .catch(() => toast.error('Failed to load enrollment details'))
            .finally(() => setLoading(false))
    }, [id])

    const toggleSubject = (subjectId) => {
        setExpanded((prev) => ({ ...prev, [subjectId]: !prev[subjectId] }))
    }

    const handleDownloadCertificate = async () => {
        if (!certificate) return
        const downloadToast = toast.loading('Downloading certificate...')
        try {
            const res = await certificateService.downloadCertificate(certificate.id)
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `certificate_${certificate.certificate_number || 'download'}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success('Certificate downloaded successfully!', { id: downloadToast })
        } catch {
            toast.error('Failed to download certificate.', { id: downloadToast })
        }
    }

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
                    Loading enrollment details...
                </div>
            </DashboardLayout>
        )
    }

    if (!enrollment) return null

    const overallProgress = progress?.progress_percentage ?? enrollment.progress_percentage ?? 0
    const lessonOnlyProgress = progress?.lesson_progress_percentage ?? overallProgress
    const courseName = enrollment.course_title || enrollment.course_name || `Course #${enrollment.course}`
    const completed = isEnrollmentCompleted(enrollment)
    const requiredAssessments = progress?.required_assessments || []
    const assessmentsComplete = progress?.assessments_complete !== false || requiredAssessments.length === 0
    const lessonsComplete = progress?.lessons_complete || Number(lessonOnlyProgress) >= 100

    return (
        <DashboardLayout>
            <main className="container" style={{ padding: 'var(--spacing-xl) 0' }}>
                <Link
                    to="/enrollments"
                    className="flex items-center gap-sm text-muted"
                    style={{ marginBottom: 'var(--spacing-lg)', fontSize: '0.9rem' }}
                >
                    <ArrowLeft size={16} /> Back to Enrollments
                </Link>

                <div style={{
                    padding: 'var(--spacing-xl)',
                    backgroundColor: 'var(--color-card-bg)',
                    borderRadius: '20px',
                    boxShadow: 'var(--shadow-sm)',
                    border: '1px solid var(--color-border)',
                    marginBottom: 'var(--spacing-xl)',
                    color: 'var(--color-text-primary)',
                }}
                >
                    <div className="flex items-center gap-md" style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                            backgroundColor: 'var(--color-accent-blue)',
                            padding: '16px',
                            borderRadius: '16px',
                        }}
                        >
                            <BookOpen size={32} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0 }}>{courseName}</h2>
                            <p className="text-muted" style={{ margin: 0 }}>
                                Status: <strong style={{ textTransform: 'capitalize' }}>{enrollment.status}</strong>
                                {enrollment.cohort_name && ` · Cohort: ${enrollment.cohort_name}`}
                            </p>
                        </div>
                    </div>

                    <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        Lesson progress — {Math.round(Number(lessonOnlyProgress))}%
                    </div>
                    <div style={{
                        height: '10px',
                        backgroundColor: 'var(--color-border)',
                        borderRadius: '999px',
                        overflow: 'hidden',
                    }}
                    >
                        <div style={{
                            width: `${Math.min(Number(lessonOnlyProgress), 100)}%`,
                            height: '100%',
                            backgroundColor: 'var(--color-primary)',
                            borderRadius: '999px',
                            transition: 'width 0.6s ease',
                        }}
                        />
                    </div>

                    {requiredAssessments.length > 0 && (
                        <div className="assessment-completion-banner">
                            <h4>
                                <ClipboardCheck size={16} style={{ display: 'inline', marginRight: 6 }} />
                                Required assessments
                            </h4>
                            <p>
                                {assessmentsComplete
                                    ? 'All required assessments are passed. Finish any remaining lessons to unlock your certificate.'
                                    : `Pass ${progress?.outstanding_required_assessments || requiredAssessments.filter((a) => !a.passed).length} remaining assessment(s) to complete this course and earn your certificate.`}
                            </p>
                            <div className="assessment-requirement-list">
                                {requiredAssessments.map((assessment) => (
                                    <div key={assessment.id} className="assessment-requirement-item">
                                        <div>
                                            <span>{assessment.title}</span>
                                            <div>
                                                <small>{assessment.assessment_type} · pass {assessment.passing_score}%</small>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-sm">
                                            <span className={`assessment-requirement-badge ${assessment.passed ? 'is-passed' : 'is-pending'}`}>
                                                {assessment.passed ? 'Passed' : 'Required'}
                                            </span>
                                            {!assessment.passed && (
                                                <Link
                                                    to={`/assessments/${assessment.id}/take`}
                                                    className="btn btn-primary"
                                                    style={{ minHeight: '2.25rem', padding: '0.35rem 0.85rem', fontSize: '0.75rem' }}
                                                >
                                                    Take
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!completed && lessonsComplete && !assessmentsComplete && (
                        <p className="text-muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                            Lessons are done — complete the required assessments above to finish the course.
                        </p>
                    )}

                    {completed && (
                        <div
                            className="flex items-center justify-between flex-wrap gap-md"
                            style={{
                                marginTop: 'var(--spacing-lg)',
                                padding: 'var(--spacing-md)',
                                backgroundColor: 'var(--color-border)',
                                borderRadius: '12px',
                            }}
                        >
                            <div>
                                <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Award size={20} color="var(--color-primary)" /> Congratulations!
                                </h4>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }} className="text-muted">
                                    You have successfully completed this course.
                                </p>
                            </div>
                            {certificate ? (
                                <button
                                    onClick={handleDownloadCertificate}
                                    className="btn btn-primary flex items-center gap-xs"
                                    style={{ padding: '8px 16px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                    Download Certificate
                                </button>
                            ) : (
                                <span className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                    Certificate generation pending...
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Course Content</h3>
                {subjects.length === 0 ? (
                    <p className="text-muted">No content available yet.</p>
                ) : (
                    <div className="flex flex-col gap-md">
                        {subjects.map((subject, idx) => (
                            <div
                                key={subject.id}
                                style={{
                                    backgroundColor: 'var(--color-card-bg)',
                                    borderRadius: '16px',
                                    boxShadow: 'var(--shadow-sm)',
                                    border: '1px solid var(--color-border)',
                                    overflow: 'hidden',
                                    color: 'var(--color-text-primary)',
                                }}
                            >
                                <button
                                    onClick={() => toggleSubject(subject.id)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: 'var(--spacing-md) var(--spacing-lg)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                    }}
                                >
                                    <span>{idx + 1}. {subject.title}</span>
                                    <ChevronRight
                                        size={18}
                                        style={{
                                            transform: expanded[subject.id] ? 'rotate(90deg)' : 'none',
                                            transition: 'transform 0.2s',
                                        }}
                                    />
                                </button>

                                {expanded[subject.id] && (
                                    <div style={{ borderTop: '1px solid var(--color-border)' }}>
                                        {(subject.lessons || []).length === 0 ? (
                                            <p className="text-muted" style={{ padding: '1rem 1.5rem' }}>No lessons yet.</p>
                                        ) : (
                                            subject.lessons.map((lesson, lIdx) => (
                                                <Link
                                                    key={lesson.id}
                                                    to={`/courses/${enrollment.course?.id || enrollment.course}`}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem',
                                                        padding: '0.85rem var(--spacing-lg)',
                                                        borderTop: lIdx > 0 ? '1px solid var(--color-border)' : 'none',
                                                        textDecoration: 'none',
                                                        color: 'inherit',
                                                        transition: 'background 0.15s',
                                                    }}
                                                >
                                                    {lesson.is_completed
                                                        ? <CheckCircle size={18} color="var(--color-primary)" />
                                                        : <Circle size={18} color="var(--color-text-muted)" />}
                                                    <span style={{ fontSize: '0.95rem' }}>
                                                        {lIdx + 1}. {lesson.title}
                                                    </span>
                                                </Link>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </DashboardLayout>
    )
}

export default EnrollmentDetail
